import { BodyStats_Input } from "@/clients/models/BodyStats_Input";
import { PickerValue, ScrollPicker } from "@/components/scroll-picker";
import { QueryKeys } from "@/constants/query-keys";
import { useGetBodyStats } from "@/hooks/requests/body-status/use-get-body-stats";
import { useUpdateBodyStats } from "@/hooks/requests/body-status/use-update-body-stats";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { cn } from "@/utils/cn";
import { IonContent, IonPage } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { OnboardingNextFab } from "../../components/onboarding-next-fab";
import { OnboardingStepShell } from "../../components/onboarding-step-shell";

type Unit = "metric" | "imperial";

const CM_MIN = 120;
const CM_MAX = 220;
const DEFAULT_CM = 170;
const DEFAULT_KG = 70;

const cmToFtIn = (cm: number) => {
  const totalInches = Math.round(cm / 2.54);
  return { ft: Math.floor(totalInches / 12), in: totalInches % 12 };
};

const ftInToCm = (ft: number, inches: number) => Math.round((ft * 12 + inches) * 2.54);

const buildRange = (min: number, max: number) =>
  Array.from({ length: max - min + 1 }, (_, i) => String(min + i));

const CM_OPTIONS = buildRange(CM_MIN, CM_MAX);
const FT_OPTIONS = buildRange(3, 7);
const IN_OPTIONS = buildRange(0, 11);

const renderItem = (value: string, selected: boolean, suffix?: string) => (
  <div
    className={cn(
      "flex h-[56px] w-full px-6 items-center justify-center text-[22px] font-semibold",
      selected ? "text-[#43246e]" : "text-[#7485a7]",
    )}
  >
    {value}
    {suffix ? <span className="ml-1">{suffix}</span> : null}
  </div>
);

interface SegmentedControlProps {
  value: Unit;
  onChange: (value: Unit) => void;
}

const SegmentedControl = ({ value, onChange }: SegmentedControlProps) => (
  <div className="flex h-11 w-[322px] items-center gap-2 rounded-[32px] bg-[#eff1f5] p-1">
    {(["metric", "imperial"] as const).map((unit) => (
      <button
        key={unit}
        type="button"
        onClick={() => onChange(unit)}
        className={cn(
          "flex h-full flex-1 items-center justify-center rounded-[37px] text-base font-medium text-black",
          value === unit && "bg-white shadow-[4px_4px_24px_0_rgba(215,214,214,0.15)]",
        )}
      >
        {unit === "metric" ? "Metric" : "Imperial"}
      </button>
    ))}
  </div>
);

const OnboardingHeightPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { bodyStats } = useGetBodyStats();
  const queryClient = useQueryClient();
  const { updateBodyStatsAsync, isUpdating } = useUpdateBodyStats();
  const { showError } = useToast();
  const hydratedRef = useRef(false);

  const [unit, setUnit] = useState<Unit>("metric");
  const [cm, setCm] = useState<number>(DEFAULT_CM);

  useEffect(() => {
    if (hydratedRef.current) return;
    const existing = Number(bodyStats?.height);
    if (existing && existing >= CM_MIN && existing <= CM_MAX) {
      setCm(existing);
      hydratedRef.current = true;
    }
  }, [bodyStats?.height]);

  const ftIn = useMemo(() => cmToFtIn(cm), [cm]);

  const metricValue: PickerValue = { height: String(cm) };
  const imperialValue: PickerValue = { ft: String(ftIn.ft), in: String(ftIn.in) };

  const handleMetricChange = (value: PickerValue) => {
    setCm(Number(value.height));
  };

  const handleImperialChange = (value: PickerValue) => {
    setCm(ftInToCm(Number(value.ft), Number(value.in)));
  };

  const handleSubmit = async () => {
    if (isUpdating) return;
    const payload: BodyStats_Input = {
      height: cm,
      heightUnit: "cm",
      startingWeight: Number(bodyStats?.startingWeight) || DEFAULT_KG,
      startingWeightUnit: "kg",
      currentWeight: Number(bodyStats?.currentWeight) || DEFAULT_KG,
      currentWeightUnit: "kg",
      targetWeight: Number(bodyStats?.targetWeight) || DEFAULT_KG,
      targetWeightUnit: "kg",
    };
    try {
      await Promise.all([preloadNextRoute(), updateBodyStatsAsync(payload)]);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.BODY_STATS] });
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <OnboardingStepShell
          icon="/assets/images/onboarding-icons/height.svg"
          title="Height"
          subtitle="This helps me calculate things more accurately"
        >
          <div className="flex w-full flex-col items-center gap-9">
            <SegmentedControl value={unit} onChange={setUnit} />
            <div className="flex w-full flex-col items-center gap-[19px]">
              <p className="text-base font-medium text-black">Height</p>
              {unit === "metric" ? (
                <ScrollPicker
                  value={metricValue}
                  onChange={handleMetricChange}
                  height={168}
                  itemHeight={56}
                  style={{ width: "100%" }}
                >
                  <ScrollPicker.Column
                    name="height"
                    options={CM_OPTIONS}
                    renderItem={(v, s) => renderItem(v, s, "cm")}
                  />
                </ScrollPicker>
              ) : (
                <ScrollPicker
                  value={imperialValue}
                  onChange={handleImperialChange}
                  height={168}
                  itemHeight={56}
                  style={{ width: "100%", gap: "8px" }}
                >
                  <ScrollPicker.Column
                    name="ft"
                    options={FT_OPTIONS}
                    renderItem={(v, s) => renderItem(v, s, "ft")}
                  />
                  <ScrollPicker.Column
                    name="in"
                    options={IN_OPTIONS}
                    renderItem={(v, s) => renderItem(v, s, "in")}
                  />
                </ScrollPicker>
              )}
            </div>
          </div>
          <OnboardingNextFab onClick={handleSubmit} disabled={isUpdating} />
        </OnboardingStepShell>
      </IonContent>
    </IonPage>
  );
};

export default OnboardingHeightPage;
